import React, { useEffect, useMemo, useState } from 'react';
import { Company, Lead, Opportunity, User } from '../types';
import { api } from '../services/apiService';
import OpportunityPDFModal from '../components/OpportunityPDFModal';

interface OpportunitiesViewProps {
  user: User;
  company: Company;
}

type OpportunityFormItem = {
  label: string;
  quantity: number;
  unitPrice: number;
};

const currencySymbol = (code?: string) => {
  switch (code) {
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    case 'CHF':
      return 'CHF';
    default:
      return '€';
  }
};

const OpportunitiesView: React.FC<OpportunitiesViewProps> = ({ user, company }) => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfOpportunity, setPdfOpportunity] = useState<Opportunity | null>(null);

  const [formLeadId, setFormLeadId] = useState('');
  const [formClientCompanyName, setFormClientCompanyName] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formComment, setFormComment] = useState('');
  const [formItems, setFormItems] = useState<OpportunityFormItem[]>([
    { label: '', quantity: 1, unitPrice: 0 }
  ]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [leadData, oppData] = await Promise.all([
        api.getLeads({ companyId: company.id, userId: user.id, role: user.role }),
        api.getOpportunities({ companyId: company.id })
      ]);
      setLeads(leadData || []);
      setOpportunities(oppData || []);
    } catch (e) {
      console.error('Erreur chargement opportunites', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [company.id, user.id, user.role]);

  const filteredOpportunities = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return opportunities;
    return opportunities.filter(o =>
      o.clientCompanyName.toLowerCase().includes(q) ||
      o.subject.toLowerCase().includes(q) ||
      (o.comment || '').toLowerCase().includes(q)
    );
  }, [opportunities, searchTerm]);

  const [oppPage, setOppPage] = useState(1);
  const oppPerPage = 25;
  const oppTotalPages = Math.ceil(filteredOpportunities.length / oppPerPage);
  const paginatedOpportunities = useMemo(() => {
    const start = (oppPage - 1) * oppPerPage;
    return filteredOpportunities.slice(start, start + oppPerPage);
  }, [filteredOpportunities, oppPage]);
  useEffect(() => { setOppPage(1); }, [searchTerm]);

  const totalFromForm = useMemo(() => {
    return formItems.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unitPrice)), 0);
  }, [formItems]);

  const resetForm = () => {
    setFormLeadId('');
    setFormClientCompanyName('');
    setFormSubject('');
    setFormComment('');
    setFormItems([{ label: '', quantity: 1, unitPrice: 0 }]);
  };

  const onSelectLead = (leadId: string) => {
    setFormLeadId(leadId);
    const lead = leads.find(l => String(l.id) === String(leadId));
    if (lead) {
      setFormClientCompanyName(lead.clientCompanyName || '');
    }
  };

  const addLine = () => {
    setFormItems(prev => [...prev, { label: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeLine = (index: number) => {
    setFormItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateLine = (index: number, patch: Partial<OpportunityFormItem>) => {
    setFormItems(prev => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const handleCreateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formLeadId) {
      alert('Selectionnez un client/prospect.');
      return;
    }
    if (!formClientCompanyName.trim()) {
      alert('Le nom du client est obligatoire.');
      return;
    }
    if (!formSubject.trim()) {
      alert("L'objet de la proposition est obligatoire.");
      return;
    }
    if (!formItems.length || formItems.some(i => !i.label.trim() || i.quantity <= 0 || i.unitPrice < 0)) {
      alert('Corrigez les lignes produits/services.');
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await api.createOpportunity(
        {
          companyId: company.id,
          leadId: formLeadId,
          clientCompanyName: formClientCompanyName.trim(),
          subject: formSubject.trim(),
          comment: formComment.trim(),
          createdBy: user.id,
          items: formItems.map(i => ({
            label: i.label.trim(),
            quantity: Number(i.quantity),
            unitPrice: Number(i.unitPrice)
          }))
        },
        { id: user.id, name: user.name }
      );
      setOpportunities(prev => [created, ...prev]);
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      console.error('Erreur creation opportunite', err);
      alert("Impossible de creer l'opportunite.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const [editingOpp, setEditingOpp] = useState<Opportunity | null>(null);
  const [confirmDeleteOppId, setConfirmDeleteOppId] = useState<string | null>(null);

  const openEditModal = (opp: Opportunity) => {
    setEditingOpp(opp);
    setFormLeadId(opp.leadId || '');
    setFormClientCompanyName(opp.clientCompanyName);
    setFormSubject(opp.subject);
    setFormComment(opp.comment || '');
    setFormItems(opp.items.map(i => ({ label: i.label, quantity: i.quantity, unitPrice: i.unitPrice })));
  };

  const handleUpdateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOpp) return;
    if (!formSubject.trim() || !formItems.length || formItems.some(i => !i.label.trim() || i.quantity <= 0 || i.unitPrice < 0)) {
      alert('Corrigez les champs obligatoires.');
      return;
    }
    setIsSubmitting(true);
    try {
      await api.updateOpportunity(editingOpp.id, {
        subject: formSubject.trim(),
        comment: formComment.trim(),
        clientCompanyName: formClientCompanyName.trim(),
        items: formItems.map(i => ({
          id: 'oppi_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          opportunityId: editingOpp.id,
          label: i.label.trim(),
          quantity: Number(i.quantity),
          unitPrice: Number(i.unitPrice),
          totalPrice: Number(i.quantity) * Number(i.unitPrice)
        }))
      });
      await loadData();
      setEditingOpp(null);
      resetForm();
    } catch (err) {
      console.error('Erreur update opportunite', err);
      alert("Impossible de modifier l'opportunite.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOpportunity = async (oppId: string) => {
    try {
      await api.deleteOpportunity(oppId);
      setOpportunities(prev => prev.filter(o => o.id !== oppId));
      setConfirmDeleteOppId(null);
    } catch (err) {
      console.error('Erreur suppression opportunite', err);
      alert("Impossible de supprimer l'opportunite.");
    }
  };

  const symbol = currencySymbol(company.currency);

  return (
    <div className="orion-dashboard space-y-5 animate-in fade-in duration-500 h-[calc(100vh-100px)] flex flex-col">
      <div className="orion-toolbar flex flex-col md:flex-row justify-between items-center gap-3 p-1.5 rounded-xl border border-slate-200 shadow-sm flex-shrink-0">
        <div className="w-full md:w-auto px-2">
          <input
            type="text"
            placeholder="Recherche client / objet / commentaire..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-96 bg-slate-50 border-none rounded-xl px-4 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700"
          />
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="orion-primary-btn text-white px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 transition-all shadow-lg"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
          NOUVELLE OPPORTUNITE
        </button>
      </div>

      <div className="orion-table-shell flex-1 rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto custom-scrollbar flex-1">
          <table className="w-full text-left border-collapse">
            <thead className="orion-table-head sticky top-0 z-10">
              <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest border-b border-slate-100">
                <th className="px-6 py-5">Client</th>
                <th className="px-6 py-5">Objet</th>
                <th className="px-6 py-5">Lignes</th>
                <th className="px-6 py-5">Montant</th>
                <th className="px-6 py-5">Date</th>
                <th className="px-6 py-5" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {!loading && paginatedOpportunities.map((opp) => (
                <tr key={opp.id} className="orion-table-row-hover group transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900">{opp.clientCompanyName}</div>
                    <div className="text-xs text-slate-400">{opp.comment || '-'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-700">{opp.subject}</td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-700">{opp.items.length}</td>
                  <td className="px-6 py-4 text-sm font-black text-indigo-600">{opp.totalAmount.toFixed(2)} {symbol}</td>
                  <td className="px-6 py-4 text-xs font-bold text-slate-500">
                    {new Date(opp.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        onClick={() => setPdfOpportunity(opp)}
                        title="PDF"
                        className="text-slate-400 hover:text-indigo-400 transition-colors p-1.5 rounded-lg hover:bg-indigo-500/10"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditModal(opp)}
                        title="Modifier"
                        className="text-slate-400 hover:text-indigo-400 transition-colors p-1.5 rounded-lg hover:bg-indigo-500/10"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {confirmDeleteOppId === opp.id ? (
                        <div className="flex gap-1">
                          <button type="button" onClick={() => handleDeleteOpportunity(opp.id)} className="px-2 py-1 rounded text-[10px] font-black bg-rose-500 text-white">Oui</button>
                          <button type="button" onClick={() => setConfirmDeleteOppId(null)} className="px-2 py-1 rounded text-[10px] font-black bg-slate-200 text-slate-600">Non</button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteOppId(opp.id)}
                          title="Supprimer"
                          className="text-slate-400 hover:text-rose-500 transition-colors p-1.5 rounded-lg hover:bg-rose-500/10"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && filteredOpportunities.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm font-medium">Aucune opportunite pour le moment.</div>
          )}
        </div>
        {oppTotalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
            <span className="text-xs font-bold text-slate-400">{((oppPage - 1) * oppPerPage) + 1}-{Math.min(oppPage * oppPerPage, filteredOpportunities.length)} sur {filteredOpportunities.length}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setOppPage(1)} disabled={oppPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'<<'}</button>
              <button onClick={() => setOppPage(p => Math.max(1, p - 1))} disabled={oppPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'<'}</button>
              {Array.from({ length: Math.min(5, oppTotalPages) }, (_, i) => {
                let page: number;
                if (oppTotalPages <= 5) { page = i + 1; }
                else if (oppPage <= 3) { page = i + 1; }
                else if (oppPage >= oppTotalPages - 2) { page = oppTotalPages - 4 + i; }
                else { page = oppPage - 2 + i; }
                return <button key={page} onClick={() => setOppPage(page)} className={`w-8 h-8 rounded-lg text-xs font-black ${oppPage === page ? 'bg-indigo-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{page}</button>;
              })}
              <button onClick={() => setOppPage(p => Math.min(oppTotalPages, p + 1))} disabled={oppPage === oppTotalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'>'}</button>
              <button onClick={() => setOppPage(oppTotalPages)} disabled={oppPage === oppTotalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'>>'}</button>
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="orion-modal-panel w-full max-w-3xl rounded-[2rem] shadow-2xl p-8 border animate-in zoom-in-95">
            <h3 className="text-xl font-black mb-6 dark:text-white">Nouvelle opportunite</h3>

            <form onSubmit={handleCreateOpportunity} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Client / Prospect</label>
                  <select
                    value={formLeadId}
                    onChange={e => onSelectLead(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Selectionner...</option>
                    {leads.map(lead => (
                      <option key={lead.id} value={lead.id}>
                        {(lead.clientCompanyName || 'Client')} - {lead.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Nom du client</label>
                  <input
                    type="text"
                    value={formClientCompanyName}
                    onChange={e => setFormClientCompanyName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Objet de la proposition</label>
                <input
                  type="text"
                  value={formSubject}
                  onChange={e => setFormSubject(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: Proposition abonnement maintenance annuelle"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Commentaire</label>
                <textarea
                  value={formComment}
                  onChange={e => setFormComment(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium outline-none h-20 resize-none"
                />
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-black uppercase tracking-widest text-slate-500">Produits / Services</div>
                  <button type="button" onClick={addLine} className="text-indigo-600 text-xs font-black">+ Ajouter ligne</button>
                </div>
                <div className="space-y-2">
                  {formItems.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        value={line.label}
                        onChange={e => updateLine(idx, { label: e.target.value })}
                        placeholder="Produit / service"
                        className="col-span-5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold"
                      />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.quantity}
                        onChange={e => updateLine(idx, { quantity: Number(e.target.value) })}
                        className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold"
                      />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.unitPrice}
                        onChange={e => updateLine(idx, { unitPrice: Number(e.target.value) })}
                        className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold"
                      />
                      <div className="col-span-2 text-xs font-black text-slate-700">
                        {(line.quantity * line.unitPrice).toFixed(2)} {symbol}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="col-span-1 text-rose-500 text-xs font-black"
                        disabled={formItems.length === 1}
                      >
                        X
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-right text-sm font-black text-indigo-600">
                  Total: {totalFromForm.toFixed(2)} {symbol}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="flex-1 py-3 text-xs font-bold text-slate-400 hover:text-slate-600">
                  Annuler
                </button>
                <button type="submit" disabled={isSubmitting} className="orion-modal-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold shadow-lg disabled:opacity-50">
                  {isSubmitting ? 'Creation...' : 'Creer opportunite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingOpp && (
        <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="orion-modal-panel w-full max-w-3xl rounded-[2rem] shadow-2xl p-8 border animate-in zoom-in-95">
            <h3 className="text-xl font-black mb-6 dark:text-white">Modifier l'opportunite</h3>
            <form onSubmit={handleUpdateOpportunity} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Nom du client</label>
                  <input type="text" value={formClientCompanyName} onChange={e => setFormClientCompanyName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Objet</label>
                  <input type="text" value={formSubject} onChange={e => setFormSubject(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Commentaire</label>
                <textarea value={formComment} onChange={e => setFormComment(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium outline-none h-20 resize-none" />
              </div>
              <div className="border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-black uppercase tracking-widest text-slate-500">Produits / Services</div>
                  <button type="button" onClick={addLine} className="text-indigo-600 text-xs font-black">+ Ajouter ligne</button>
                </div>
                <div className="space-y-2">
                  {formItems.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input type="text" value={line.label} onChange={e => updateLine(idx, { label: e.target.value })} placeholder="Produit / service" className="col-span-5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold" />
                      <input type="number" step="0.01" min="0" value={line.quantity} onChange={e => updateLine(idx, { quantity: Number(e.target.value) })} className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold" />
                      <input type="number" step="0.01" min="0" value={line.unitPrice} onChange={e => updateLine(idx, { unitPrice: Number(e.target.value) })} className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold" />
                      <div className="col-span-2 text-xs font-black text-slate-700">{(line.quantity * line.unitPrice).toFixed(2)} {symbol}</div>
                      <button type="button" onClick={() => removeLine(idx)} className="col-span-1 text-rose-500 text-xs font-black" disabled={formItems.length === 1}>X</button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-right text-sm font-black text-indigo-600">Total: {totalFromForm.toFixed(2)} {symbol}</div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setEditingOpp(null); resetForm(); }} className="flex-1 py-3 text-xs font-bold text-slate-400 hover:text-slate-600">Annuler</button>
                <button type="submit" disabled={isSubmitting} className="orion-modal-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold shadow-lg disabled:opacity-50">
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pdfOpportunity && (
        <OpportunityPDFModal
          opportunity={pdfOpportunity}
          companyInfo={{ name: company.name, currency: company.currency, address: company.address, email: company.email, phone: company.phone, siret: company.siret }}
          onClose={() => setPdfOpportunity(null)}
        />
      )}
    </div>
  );
};

export default OpportunitiesView;
